// src/pages/Login.js
import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "../providers/AuthProvider";
import { useMsal } from "@azure/msal-react";
import { updateFavicon, updatePageTitle } from "../utils/favicon";
import apiService from "../services/apiService";
import "./styles/Login.css";

function Login() {
  const { instance } = useMsal();
  const navigate = useNavigate();
  const { login, user } = useAuth();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsAccepted, setTermsAccepted] = useState(false);

  useEffect(() => {
    const organization = user?.user?.organization;
    if (organization) {
      if (organization.favicon_url) {
        updateFavicon(organization.favicon_url);
      }
      if (organization.name) {
        updatePageTitle(organization.name, "Login");
      }
    }
  }, [user?.user?.organization]);

  const handleLogin = async () => {
    setIsLoading(true);
    setError("");

    try {
      // Get Microsoft login popup
      const loginResponse = await instance.loginPopup();

      if (loginResponse && loginResponse.idToken) {
        // Use centralized API service
        const response = await apiService.loginWithMicrosoft(loginResponse.idToken);

        if (response.status === 200) {
          const data = response.data;

          // Update favicon and title if organization data exists
          const organization = data.user?.organization;
          if (organization) {
            if (organization.favicon_url) {
              updateFavicon(organization.favicon_url);
            }
            if (organization.name) {
              updatePageTitle(organization.name);
            }
          }

          // Store user data in auth context
          login(data);

          // Navigate to home page
          navigate("/home");
        } else {
          setError("Failed to authenticate. Please try again.");
        }
      } else {
        setError("Login cancelled or failed. Please try again.");
      }
    } catch (error) {
      console.error("Login failed:", error);
      setError(error.message || "An error occurred during login. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="gradient-bg"></div>
      <div className="shape shape-1"></div>
      <div className="shape shape-2"></div>
      <div className="shape shape-3"></div>

      <div className="login-content">
        <div className="login-card">
          <div className="brand-section">
            <h1 className="brand-title">Welcome Back</h1>
            <p className="brand-subtitle">
              Sign in to continue to your account
            </p>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {/* Terms acceptance */}
          <div className="terms-acceptance">
            <label className="terms-label">
              <input
                type="checkbox"
                className="terms-checkbox"
                checked={termsAccepted}
                onChange={(e) => setTermsAccepted(e.target.checked)}
              />
              <span>
                I have read and agree to the{" "}
                <Link to="/terms" target="_blank" rel="noopener noreferrer" className="terms-link">
                  Terms of Use
                </Link>{" "}
                and{" "}
                <Link to="/privacy" target="_blank" rel="noopener noreferrer" className="terms-link">
                  Privacy Policy
                </Link>
              </span>
            </label>
          </div>

          {/* Login button */}
          <button
            className={`login-button ${isLoading ? "loading" : ""} ${!termsAccepted ? "disabled" : ""}`}
            onClick={handleLogin}
            disabled={isLoading || !termsAccepted}
            title={!termsAccepted ? "Please accept the Terms of Use and Privacy Policy to continue" : ""}
          >
            {isLoading ? (
              <>
                <span className="spinner"></span>
                Signing in...
              </>
            ) : (
              <>
                Log in
              </>
            )}
          </button>

        </div>
      </div>
    </div>
  );
}

export default Login;